import {
  ViveApiBaseService,
  ViveApiRequestOptions
} from './vive-api-base.service';
import {
  AuthPublicApiTokenResponseDto,
  AuthSessionImpersonationDto,
  AuthTokenDto,
  AuthTokenResponseDto,
  SessionDto
} from './vive-api-models';

class AuthService extends ViveApiBaseService {
  public async getPublicApiToken(params: {
    options?: ViveApiRequestOptions;
    pathParams: { apiAccessKey: string };
  }): Promise<AuthPublicApiTokenResponseDto> {
    const { options, pathParams } = params;
    const newTokenResponse = await this.sendGet<AuthPublicApiTokenResponseDto>({
      apiEndpoint: 'auth/public-api-token/:apiAccessKey',
      pathParams,
      options
    });

    return newTokenResponse;
  }

  public async getAuthToken(params: {
    options?: ViveApiRequestOptions;
    data: AuthTokenDto;
  }): Promise<AuthTokenResponseDto> {
    const { options, data } = params;
    const newTokenResponse = await this.sendPost<AuthTokenResponseDto>({
      apiEndpoint: 'auth/token',
      options,
      authRequired: false,
      data
    });

    // Updates the current user token using the new token
    if (newTokenResponse?.token) {
      ViveApiBaseService.setUserToken(newTokenResponse.token);
    }

    return newTokenResponse;
  }

  public async refreshAuthToken(params?: {
    options?: ViveApiRequestOptions;
  }): Promise<AuthTokenResponseDto> {
    const { options } = params ?? {};
    const refreshTokenResponse = await this.sendPost<AuthTokenResponseDto>({
      apiEndpoint: 'auth/refresh',
      options
    });

    // Updates the current user token using the refresh token
    if (refreshTokenResponse?.token) {
      ViveApiBaseService.setUserToken(refreshTokenResponse.token);
    }

    return refreshTokenResponse;
  }

  public async logout(params?: {
    options?: ViveApiRequestOptions;
  }): Promise<void> {
    const { options } = params ?? {};
    await this.sendPost<void>({
      apiEndpoint: 'auth/logout',
      options
    });

    // Clears the current user token on logout
    ViveApiBaseService.setUserToken(null);
  }

  public async createSession(params: {
    options?: ViveApiRequestOptions;
    data: AuthTokenDto;
  }): Promise<SessionDto> {
    const { options, data } = params;
    const session = await this.sendPost<SessionDto>({
      apiEndpoint: 'auth/session',
      options,
      data
    });

    return session;
  }

  public async getSession(params?: {
    options?: ViveApiRequestOptions;
  }): Promise<SessionDto> {
    const { options } = params ?? {};
    const session = await this.sendGet<SessionDto>({
      apiEndpoint: 'auth/session',
      options
    });

    return session;
  }

  // Null on a plain sign-out (204). While impersonating, the restored staff
  // session.
  public async deleteSession(params?: {
    options?: ViveApiRequestOptions;
  }): Promise<SessionDto | null> {
    const { options } = params ?? {};
    const session = await this.sendDelete<SessionDto | null>({
      apiEndpoint: 'auth/session',
      options
    });

    return session;
  }

  // Ends every session and token of the signed-in person, this browser's
  // included. Cookie sessions only; refused with 409 while impersonating.
  public async signOutEverywhere(params?: {
    options?: ViveApiRequestOptions;
  }): Promise<void> {
    const { options } = params ?? {};
    await this.sendPost<void>({
      apiEndpoint: 'auth/session/sign-out-everywhere',
      options
    });
  }

  public async refreshSession(params?: {
    options?: ViveApiRequestOptions;
  }): Promise<SessionDto> {
    const { options } = params ?? {};
    const session = await this.sendPost<SessionDto>({
      apiEndpoint: 'auth/session/refresh',
      options
    });

    return session;
  }

  // Turns a Bearer token (the Connect link's) into a browser session.
  public async exchangeSession(params: {
    connectToken: string;
    options?: ViveApiRequestOptions;
  }): Promise<SessionDto> {
    const { connectToken, options } = params;
    const session = await this.sendPost<SessionDto>({
      apiEndpoint: 'auth/session/exchange',
      options: {
        ...options,
        additionalHeaders: {
          ...options?.additionalHeaders,
          Authorization: `Bearer ${connectToken}`
        }
      }
    });

    return session;
  }

  // Swaps the browser session for one acting as the client or user named.
  public async startImpersonation(params: {
    options?: ViveApiRequestOptions;
    data: AuthSessionImpersonationDto;
  }): Promise<SessionDto> {
    const { options, data } = params;
    const session = await this.sendPost<SessionDto>({
      apiEndpoint: 'auth/session/impersonation',
      options,
      data
    });

    return session;
  }

  // "Return to my account": the restored staff session.
  public async endImpersonation(params?: {
    options?: ViveApiRequestOptions;
  }): Promise<SessionDto> {
    const { options } = params ?? {};
    const session = await this.sendDelete<SessionDto>({
      apiEndpoint: 'auth/session/impersonation',
      options
    });

    return session;
  }
}

export const authService = new AuthService();
